import { CheckCircleOutlined, ExclamationCircleOutlined, ReloadOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { Card, Divider, Tag, Table, Button, Typography } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import { retrieveAegisPosture } from '../../methods/redux/reducers/compliance';

const Container = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	gap: 1em;
`;

const AegisPosture = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { aegisPosture } = useAppSelector((store) => store.compliance);
	const [loading, setLoading] = useState(false);

	const fetchData = async () => {
		setLoading(true);
		await dispatch(retrieveAegisPosture());
		setLoading(false);
	};

	useEffect(() => {
		fetchData();
	}, []);

	const columns = [
		{
			title: 'Security Check',
			dataIndex: 'name',
			key: 'name',
			render: (name: string) => <strong style={{ textTransform: 'capitalize' }}>{name.replace(/_/g, ' ')}</strong>,
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (status: string) => {
				let color = 'default';
				let icon = <ExclamationCircleOutlined />;
				
				if (status === 'pass') {
					color = 'green';
					icon = <CheckCircleOutlined />;
				} else if (status === 'warn') {
					color = 'orange';
					icon = <ExclamationCircleOutlined />;
				} else if (status === 'fail') {
					color = 'red';
					icon = <ExclamationCircleOutlined />;
				}

				return (
					<Tag color={color} icon={icon}>
						{status?.toUpperCase()}
					</Tag>
				);
			},
		},
		{
			title: 'Details',
			dataIndex: 'detail',
			key: 'detail',
			render: (detail: string) => (
				<code style={{ background: '#f5f5f5', padding: '2px 5px', borderRadius: '4px' }}>
					{detail}
				</code>
			),
		},
	];

	return (
		<Container>
			<Card>
				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<div style={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
						<Button 
							type="text" 
							icon={<ArrowLeftOutlined />} 
							onClick={() => navigate(-1)} 
							style={{ fontSize: '18px' }}
						/>
						<div>
							<h1>Aegis Security Posture</h1>
							<p>Real-time audit of runtime configurations and security headers.</p>
						</div>
					</div>
					<div style={{ textAlign: 'right' }}>
						<Button 
							type="primary" 
							icon={<ReloadOutlined />} 
							onClick={fetchData} 
							loading={loading}
							style={{ marginBottom: '5px' }}
						>
							Re-scan Posture
						</Button>
						{aegisPosture?.checkedAt && (
							<div style={{ fontSize: '12px', color: '#8c8c8c' }}>
								Last checked: {moment(aegisPosture.checkedAt).fromNow()}
							</div>
						)}
					</div>
				</div>
				<Divider />

				<div style={{ display: 'flex', gap: '2em', marginBottom: '2em' }}>
					<Card size="small" title="Overall Grade" style={{ flex: 1 }}>
						<Tag color={aegisPosture?.overall === 'pass' ? 'green' : aegisPosture?.overall === 'warn' ? 'orange' : 'red'} style={{ fontSize: '16px', padding: '5px 15px' }}>
							{aegisPosture?.overall?.toUpperCase() || 'UNKNOWN'}
						</Tag>
					</Card>
					<Card size="small" title="Passed" style={{ flex: 1 }}>
						<strong style={{ color: '#52c41a', fontSize: '20px' }}>{aegisPosture?.counts?.pass || 0}</strong>
					</Card>
					<Card size="small" title="Warnings" style={{ flex: 1 }}>
						<strong style={{ color: '#faad14', fontSize: '20px' }}>{aegisPosture?.counts?.warn || 0}</strong>
					</Card>
					<Card size="small" title="Failed" style={{ flex: 1 }}>
						<strong style={{ color: '#ff4d4f', fontSize: '20px' }}>{aegisPosture?.counts?.fail || 0}</strong>
					</Card>
				</div>

				<Table
					columns={columns}
					dataSource={aegisPosture?.checks || []}
					rowKey="name"
					pagination={false}
					loading={loading}
				/>
			</Card>
		</Container>
	);
};

export default AegisPosture;
